/** Pure helpers of the AHJ and the structural site loads in the Permit workspace (tested in permitAhj.test.ts).
 * Text comes from the `permit` catalogs through the caller's `t`; `codeLine` and `STAMP_LABEL` without `t` keep their
 * English for the admin console's "Permit AHJs" (another lane's page). */
import type { MessageKey } from "../i18n/keys";
import type { Tr } from "./permits.ts";

export type StampRule = "yes" | "no" | "unknown";

export interface AhjCodes {
  nec?: string | null;
  irc?: string | null;
  ibc?: string | null;
  ifc?: string | null;
  asce?: string | null;
  effective?: string | null;
}

/** "NEC 2023 · IRC 2024 · IBC 2024 · ASCE 7-22 · since 2025-08-01"; "Code editions not on file" without any. */
export function codeLine(codes: AhjCodes | null | undefined, t?: Tr): string {
  const c = codes ?? {};
  const parts = [
    c.nec && `NEC ${c.nec}`,
    c.irc && `IRC ${c.irc}`,
    c.ibc && `IBC ${c.ibc}`,
    c.ifc && `IFC ${c.ifc}`,
    c.asce && `ASCE ${c.asce}`,
  ].filter(Boolean) as string[];
  if (!t) {
    if (!parts.length) return "Code editions not on file";
    return parts.join(" · ") + (c.effective ? ` · since ${c.effective}` : "");
  }
  if (!parts.length) return t("permit.ahj.codesNotOnFile");
  const codes_ = parts.join(" · ");
  return c.effective ? t("permit.ahj.codesSince", { codes: codes_, date: c.effective }) : codes_;
}

export const STAMP_LABEL: Record<StampRule, string> = { yes: "required", no: "not required", unknown: "not on file" };

const STAMP_KEY: Record<"Structural" | "Electrical", Record<StampRule, MessageKey>> = {
  Structural: { yes: "permit.ahj.stamp.structural.yes", no: "permit.ahj.stamp.structural.no", unknown: "permit.ahj.stamp.structural.unknown" },
  Electrical: { yes: "permit.ahj.stamp.electrical.yes", no: "permit.ahj.stamp.electrical.no", unknown: "permit.ahj.stamp.electrical.unknown" },
};

export function stampText(t: Tr, kind: "Structural" | "Electrical", rule: string | null | undefined): string {
  const keys = STAMP_KEY[kind];
  return t(keys[rule as StampRule] ?? keys.unknown);
}

export function solarAppText(t: Tr, value: boolean | null | undefined): string | null {
  if (value == null) return null;
  return value ? t("permit.ahj.solarAppYes") : t("permit.ahj.solarAppNo");
}

export interface LoadValue {
  value: number | null;
  source: string;
  kind: "installer" | "ahj" | "provider" | "none";
  suggested?: number | null;
  suggested_source?: string | null;
}

/** Where a site load comes from, in a few words for the Structure step. */
export function loadSource(t: Tr, load: LoadValue | null | undefined): string {
  if (!load) return "";
  switch (load.kind) {
    case "installer":
      return load.suggested != null ? t("permit.loads.yourValueReplaces", { value: load.suggested, source: load.suggested_source ?? "" }) : t("permit.loads.yourValue");
    case "ahj":
    case "provider":
      return load.source;
    default:
      return t("permit.loads.notOnFile");
  }
}

/** The value the field shows as its placeholder when the installer has not entered one. */
export function suggestedValue(load: LoadValue | null | undefined): number | null {
  if (!load) return null;
  if (load.kind === "installer") return load.suggested ?? null;
  return load.value;
}

/** Wind zones of a face, most suction first, as "zone 3: every 16 in". */
export interface ZoneRow {
  zone: string;
  panels: number;
  exposed: number;
  gcp: number;
  attachment_spacing_m: number | null;
  status: "pass" | "problem" | "info";
}

export function zonesBySuction<T extends ZoneRow>(zones: T[] | null | undefined): T[] {
  return [...(zones ?? [])].sort((a, b) => a.gcp - b.gcp || a.zone.localeCompare(b.zone));
}
